import { useMemo } from 'react';
import { Table, Column } from '@cb/apricot-react';
import './TestDayExamTimings.scss';
import { buildTableData, TableRow } from './helper';
import { accommodationTypes } from '../../../../constants/accommodations/accommodationTypes';
import { useQuery } from '@apollo/client/react';
import { GET_ASSESSMENT_TIMINGS } from '../../../../apollo/queries';
import { EVENT } from '@tdtk/types';
import Spinner from '../../../ui/loading/SpinnerWrapper';

export default function TestDayExamTimings({
  assessmentId,
  tdtkAsmtType,
  shouldShowSection3,
}: {
  assessmentId?: string;
  tdtkAsmtType?: string;
  shouldShowSection3: boolean;
}) {
  const { loading, error, data } = useQuery(GET_ASSESSMENT_TIMINGS, {
    variables: { input: { assessmentId: assessmentId ?? '' } },
    skip: !assessmentId,
  });

  // For each section in standardTime, we want to create a row that includes the standard time and the accommodated times for each accommodation type.
  const combinedTableData = useMemo(() => {
    const assessmentTime = data?.readAssessmentTimings;
    if (!assessmentTime?.sectionTimings) return [];

    const sections = assessmentTime.sectionTimings;

    let isSatExam: boolean;
    switch (tdtkAsmtType) {
      case EVENT.EVENT_TYPES.INSCHOOL_SAT:
      case EVENT.EVENT_TYPES.WEEKEND_SAT:
      case EVENT.EVENT_TYPES.WEEKENDATSCHOOL_SAT:
        isSatExam = true;
        break;
      default:
        isSatExam = false;
        break;
    }

    // Only slice the last two sections (Break + Essay) for SAT exams when section 3 should not be shown.
    // Non-SAT exams (AP, PSAT, etc.) don't have section 3 data, so slicing would remove real sections.
    if (isSatExam && !shouldShowSection3) {
      return buildTableData(sections.slice(0, -2));
    }

    return buildTableData(sections);
  }, [data, tdtkAsmtType, shouldShowSection3]);

  const totalColumns = 2 + accommodationTypes.length; // 1 for section title + 1 for standard time + 2 for accommodation types
  const isSeparator = (row: TableRow) => !!row._isSeparator;

  return (
    <div className='box-card py-3 px-3 mb-4'>
      {loading ? (
        <div className='d-flex justify-content-center'>
          <Spinner />
        </div>
      ) : error || combinedTableData.length === 0 ? (
        <div className='tdtk-summary-accordion__placeholder' data-automation='timing-summary-placeholder'>
          No timing data available.
        </div>
      ) : (
        <Table
          data={combinedTableData}
          noStretch
          responsive
          className='cb-no-border-left-right cb-border-bottom exam-timings-table'
        >
          <>
            <Column
              field='sectionTitle'
              title=''
              rowHeader
              className='body-only-callout'
              withColScope={false}
              headerComponent={() => <></>}
              onColSpan={(row) => (isSeparator(row as TableRow) ? totalColumns : 1)}
              classNameFunc={(_, row) => (isSeparator(row as TableRow) ? 'separator-row' : 'body-only-callout')}
            />
            <Column
              field='standardTime'
              title='Standard Time'
              className='header-callout'
              onColSpan={(row) => (isSeparator(row as TableRow) ? 0 : 1)}
            />
            {accommodationTypes.map((a) => (
              <Column
                key={a.title}
                field={a.title}
                title={a.title}
                className='header-callout'
                onColSpan={(row) => (isSeparator(row as TableRow) ? 0 : 1)}
              />
            ))}
          </>
        </Table>
      )}
    </div>
  );
}
